import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { BaseSyntheticEvent, CSSProperties } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	dataTestId?: string;
	onClick?: (event: BaseSyntheticEvent) => void;
	style?: CSSProperties;
	src: string;
}

/**
 * Loads a context asset (illustration, screenshot, logo) from the assets server.
 *
 * Icons do NOT come through here — they are compiled into the bundle, see Icon.tsx.
 * SVGs used to be fetched and inlined so CSS could recolour them; nothing left here
 * needs that (none of the remaining artwork uses currentColor), so everything renders
 * as a plain <img>: no fetch/sanitise/parse on the main thread, native lazy loading and
 * HTTP caching, and no way for one dropped request to blank the element for good.
 *
 * !!WARNING: reads the assets origin from the runtime global config!!
 */
const AssetsLoader = ({ className, dataTestId, style, ...props }: Props) => {
	const { assetsUrl } = useGlobalConfig();

	if (!assetsUrl) {
		const err = new Error('No URL for ASSETS:');
		logger.error(err, 'No URL for ASSETS in AssetsLoader', { props });
		return null;
	}

	// Vector assets keep the `svg` class: consumers size them through --size.
	const isVector = props.src.endsWith('.svg');

	return (
		<img
			alt=""
			crossOrigin="anonymous"
			data-testid={dataTestId}
			className={`${className} ${isVector ? 'svg' : 'img'}`}
			style={style}
			{...props}
			src={`${assetsUrl}${props.src}`}
		/>
	);
};

const Styled = styled(AssetsLoader)`
	&.svg {
		box-sizing: content-box;
		--size: 0.75rem;
		width: var(--size);
		height: var(--size);
	}
`;

export { Styled as AssetsLoader };
